import { Facilities, Period, useFileSaver } from '@geovelo-frontends/commons';
import { CloudDownload } from '@mui/icons-material';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogProps,
  DialogTitle,
  LinearProgress,
  SvgIconProps,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  Typography,
} from '@mui/material';
import { deepOrange, green } from '@mui/material/colors';
import { useTheme } from '@mui/material/styles';
import { useQuery } from '@tanstack/react-query';
import { useContext, useEffect, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';

import { AppContext } from '../../app/context';
import { getChildrenTypeKey } from '../../utils/cyclability-zone';
import { fetchFRDepartments, getMonthsStatsPeriod, statsKeys } from '../../utils/zone-stats';

import facilities from './facilities';

type TKey = 'name' | Facilities | 'all';

type TCell = { [key in TKey]?: { value: string | number; diff?: number } };

interface IRow {
  id: number;
  cells: TCell;
}

type TProps = Omit<DialogProps, 'onClose'> & { onClose: () => void };

function ChildrenDialog({ ...props }: TProps): React.JSX.Element {
  const [childrenTypeKey, setChildrenTypeKey] = useState(getChildrenTypeKey(null));
  const [orderBy, setOrderBy] = useState<TKey>('all');
  const [order, setOrder] = useState<'asc' | 'desc'>('desc');
  const [rows, setRows] = useState<IRow[]>([]);
  const [sortedRows, sortRows] = useState<IRow[]>([]);
  const {
    zone: { map: zoneMap, childrenMap, current: currentZone },
    stats: { byZone, periods, selectedIndex: selectedPeriodIndex, selectedKey: selectedPeriodKey },
  } = useContext(AppContext);
  const {
    i18n: { language },
    t,
  } = useTranslation();
  const { transitions } = useTheme();
  const { downloadCSV } = useFileSaver();

  const showFRDpts =
    currentZone?.administrativeLevel === 'country' &&
    currentZone.countryCode === 'fr' &&
    (!process.env.REACT_APP_CONFIG || process.env.REACT_APP_CONFIG === 'france');
  const dptsPeriod = selectedPeriodKey ? getMonthsStatsPeriod(selectedPeriodKey, 2) : null;
  const { data: frDpts, isLoading: frDptsLoading } = useQuery({
    queryKey: statsKeys.children(currentZone?.id ?? -1, 'DEPARTMENT', dptsPeriod || 'all_time'),
    queryFn: () => fetchFRDepartments(dptsPeriod as Period),
    enabled: props.open && Boolean(showFRDpts) && Boolean(dptsPeriod),
  });

  useEffect(() => {
    const children =
      currentZone &&
      (showFRDpts
        ? (frDpts || []).map((zone) => ({ zone, stats: zone.stats }))
        : childrenMap[currentZone.id]
            ?.map((id) => zoneMap[id])
            .filter(Boolean)
            .map((zone) => ({ zone, stats: byZone[zone.id] || [] })));

    if (props.open && children && selectedPeriodKey) {
      const prevPeriodKey =
        (periods && selectedPeriodIndex !== null && periods[selectedPeriodIndex - 1]?.key) || null;

      setRows([
        ...children.map(({ zone: { id, name, administrativeLevel, countryCode, ref }, stats }) => {
          const periodStats = stats.find(({ dateAsKey }) => dateAsKey === selectedPeriodKey);
          const prevStats =
            (prevPeriodKey && stats.find(({ dateAsKey }) => dateAsKey === prevPeriodKey)) || null;
          const totalDistance = Math.round(periodStats?.distances.all || 0);
          const cells: TCell = {
            name: {
              value:
                countryCode === 'fr' && administrativeLevel === 'department'
                  ? `${ref} - ${name}`
                  : name,
            },
            all: {
              value: totalDistance,
              diff: (prevStats && Math.round(totalDistance - prevStats.distances.all)) || undefined,
            },
          };

          facilities.forEach(({ key }) => {
            const distance = Math.round(periodStats?.distances[key] || 0);
            const diff =
              (prevStats && Math.round(distance - prevStats.distances[key])) || undefined;

            cells[key] = { value: distance, diff };
          });

          return { id, cells };
        }),
      ]);
    } else if (!props.open) {
      setTimeout(() => {
        setRows([]);
      }, transitions.duration.leavingScreen);
    } else {
      setRows([]);
    }
  }, [props.open, currentZone, childrenMap, selectedPeriodKey, byZone, frDpts]);

  useEffect(() => {
    if (currentZone) {
      const { administrativeLevel, countryCode } = currentZone;

      setChildrenTypeKey(
        getChildrenTypeKey(
          (administrativeLevel === 'country' &&
            countryCode === 'fr' &&
            (!process.env.REACT_APP_CONFIG || process.env.REACT_APP_CONFIG === 'france') &&
            'region') ||
            administrativeLevel,
        ),
      );
    }
  }, [currentZone]);

  useEffect(() => {
    sortRows([
      ...rows.sort(({ cells: aCells }, { cells: bCells }) => {
        const aValue = aCells[orderBy]?.value;
        const bValue = bCells[orderBy]?.value;

        if (typeof aValue === 'number' && typeof bValue === 'number')
          return order === 'asc' ? aValue - bValue : bValue - aValue;
        if (typeof aValue === 'string' && typeof bValue === 'string')
          return order === 'asc' ? aValue.localeCompare(bValue) : bValue.localeCompare(aValue);

        return 0;
      }),
    ]);
  }, [rows, orderBy, order]);

  function handleSort(key: TKey) {
    setOrder(
      orderBy === key ? (order === 'asc' ? 'desc' : 'asc') : key === 'name' ? 'asc' : 'desc',
    );
    setOrderBy(key);
  }

  function handleDownload() {
    if (!currentZone) return;

    downloadCSV(
      `${currentZone.name.replace(/\s+/g, '_').toLowerCase()}_${selectedPeriodKey}.csv`,
      headers.map(({ key, title }) => (key === 'name' ? title : `${title} (kms)`)),
      rows.map(({ cells }) =>
        headers.map(({ key }) => {
          const value = cells[key]?.value;

          return key === 'name' ? `"${value || ''}"` : value || 0;
        }),
      ),
    );
  }

  const headers: Array<{
    Icon?: (props: SvgIconProps) => React.JSX.Element;
    key: TKey;
    title: string;
  }> = [
    { key: 'name', title: t(childrenTypeKey, { count: 1 }) },
    { key: 'all', title: 'Total' },
    ...facilities.map(({ key, Icon, titleKey }) => ({
      key,
      Icon,
      title: t(titleKey),
    })),
  ];

  return (
    <Dialog fullWidth maxWidth="xl" {...props}>
      <DialogTitle>
        <Trans
          i18nKey="bicycle_facilities.stats.children_dialog.title"
          values={{
            type:
              currentZone?.administrativeLevel !== 'department' || language !== 'fr'
                ? t(childrenTypeKey, { context: 'other' }).toLowerCase()
                : t(childrenTypeKey, { context: 'other' }),
          }}
        />
      </DialogTitle>
      {frDptsLoading && <LinearProgress />}
      <TableContainer>
        <Table stickyHeader>
          <TableHead>
            <TableRow>
              {headers.map(({ key, Icon, title }) => (
                <TableCell key={key} sortDirection={orderBy === key ? order : false}>
                  <TableSortLabel
                    active={orderBy === key}
                    direction={orderBy === key ? order : 'asc'}
                    onClick={() => handleSort(key)}
                  >
                    <Box alignItems="center" component="span" display="flex" gap={1.5}>
                      {Icon && <Icon color="action" fontSize="small" />}
                      <Typography component="span" fontWeight={700} variant="body2">
                        <span style={{ whiteSpace: 'nowrap' }}>{title}</span>
                        {key !== 'name' ? <> (kms)</> : <></>}
                      </Typography>
                    </Box>
                  </TableSortLabel>
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {sortedRows.map(({ id, cells }) => (
              <TableRow key={id}>
                {headers.map(({ key }) => {
                  const cell = cells[key];
                  if (!cell) return <TableCell key={key} />;

                  const { value, diff } = cell;

                  return (
                    <TableCell key={key} sx={{ whiteSpace: key !== 'name' ? 'nowrap' : undefined }}>
                      {value}
                      <Typography
                        className={diff && diff >= 0 ? green[500] : deepOrange[500]}
                        variant="caption"
                      >
                        {diff && (
                          <>
                            &nbsp; ({diff > 0 ? '+' : '-'}
                            {Math.abs(diff)})
                          </>
                        )}
                      </Typography>
                    </TableCell>
                  );
                })}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
      <DialogActions>
        <Button
          color="primary"
          onClick={handleDownload}
          size="small"
          startIcon={<CloudDownload />}
          variant="outlined"
        >
          CSV
        </Button>
        <Button onClick={props.onClose} size="small" variant="outlined">
          <Trans i18nKey="commons.actions.close" />
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export default ChildrenDialog;
